import type { DatasetDescriptor, FieldPresence } from '@cdr-clarity/contracts';
import type { WorkerClient } from '../runtime/worker-client.js';
import { FieldState } from './field-state.js';
import { readFieldPreset } from './field-preset-preference.js';
import { RecordsState } from './records-state.js';
import type { PreferenceStorage } from './view-preferences.js';
import { RulesState } from './rules-state.js';
import { UnifiedRecordsState } from './unified-records-state.js';

export async function importWorkspace(
  client: WorkerClient,
  descriptor: DatasetDescriptor,
  storage: PreferenceStorage,
) {
  const capabilities = await client.request({ operation: 'discover' });
  const presence = new Map<string, FieldPresence>();
  for (const collection of ['cdr', 'cmr'] as const) {
    const result = await client.request({
      operation: 'presence',
      scope: {
        datasetId: descriptor.id,
        revision: descriptor.revision,
        classificationRevision: descriptor.classificationRevision,
        sources: [],
        collection,
      },
    });
    for (const field of result) {
      const previous = presence.get(field.field);
      presence.set(
        field.field,
        previous
          ? {
              ...field,
              total: field.total + previous.total,
              empty: field.empty + previous.empty,
              nonempty: field.nonempty + previous.nonempty,
              complete: field.complete && previous.complete,
            }
          : field,
      );
    }
  }
  const fields = new FieldState(
    capabilities,
    [...presence.values()],
    storage,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const preset = readFieldPreset(storage);
  if (capabilities.manifest.presets.some((item) => item.id === preset)) fields.applyPreset(preset);
  const records = new RecordsState(fields, descriptor, (query) =>
    client.request({ operation: 'query', query }),
  );
  const rules = new RulesState(
    client,
    storage,
    capabilities.platform,
    () => crypto.randomUUID(),
    () => records.getSnapshot().scope,
    (updated) => records.adoptRevision(updated),
  );
  const unified = new UnifiedRecordsState(
    records,
    await client.request({ operation: 'relationshipFields' }),
    (query) => client.request({ operation: 'relationshipQuery', query }),
  );
  return { fields, records, descriptor, rules, unified };
}
export type Workspace = Awaited<ReturnType<typeof importWorkspace>>;
